<template>
    <div class="ps o-main-content-area" data-testid="phone-sheet-page">
        <div class="o-page-top-heading c-responsive-fixwidth">
            <h1 class="h1">Phone sheet</h1>
            <p class="ps-lede">
                Every contact across the locals in this database, with whether calls
                may be forwarded to them. Read-only.
            </p>
        </div>

        <div class="ps-controls">
            <label>Local
                <select v-model="local" data-testid="ps-local">
                    <option value="">All locals</option>
                    <option v-for="l in locals" :key="l" :value="l">{{ l }}</option>
                </select>
            </label>
            <button class="ps-refresh" @click="load" data-testid="ps-refresh">Refresh</button>
            <span class="ps-count" data-testid="ps-count">
                {{ visible.length }} of {{ rows.length }} · {{ callableCount }} callable
            </span>
        </div>

        <p v-if="denied" class="ps-denied" data-testid="ps-denied">
            This page is Super Admin only.
        </p>
        <p v-else-if="error" class="ps-error" data-testid="ps-error">{{ error }}</p>

        <div v-else v-loading="loading">
            <!-- ⚠ AN EMPTY SHEET IS A RESULT, NOT A FAULT. Today it is 2 rows from
                 16 contacts across 3 locals, because only 2 have consented. "No
                 data" would read as a broken page and send somebody looking for a
                 bug that is not there, so the message says what is missing and
                 where it is set. -->
            <p v-if="!rows.length" class="ps-empty" data-testid="ps-empty">{{ emptyText }}</p>

            <table v-else class="ps-table" data-testid="ps-table">
                <thead>
                    <tr>
                        <th>Local</th>
                        <th>Position</th>
                        <th>Contact</th>
                        <th>Number</th>
                        <th>Forwarding</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- ⚠ ONE ROW PER POSITION. Not per person, and never deduped by
                         number: on iatse24 one person holds three positions with the
                         same number and only one of them consents. Collapsing them
                         would turn one consent into three. -->
                    <tr v-for="r in visible" :key="r.id" :data-testid="'ps-row-' + r.id">
                        <td>{{ r.local }}</td>
                        <td><strong>{{ r.position }}</strong></td>
                        <td class="ps-contact">{{ r.contact || '—' }}</td>
                        <td>
                            <span v-if="r.e164" :class="r.dialable ? 'ps-number' : 'ps-number ps-number--unreadable'">{{ r.e164 }}</span>
                            <span v-else class="text-muted">—</span>
                            <small v-if="r.e164 && !r.dialable" class="ps-unreadable-note">not a readable number</small>
                        </td>
                        <!-- ⚠ A TAG, NOT A FILTER (ed). The rows a local most needs
                             to see are the ones the filtered sheet hid: a consent
                             with nowhere to send the call, and a position nobody
                             has opted in yet. -->
                        <!-- #1917 — the CELL carries the green, not just the 11px pill.
                             ⚠ The pill and its text stay: the colour is an addition,
                             never the carrier. Someone reading a printout or a
                             screenshot still gets the answer in words. -->
                        <td :class="cellClassFor(r)" :data-testid="'ps-cell-' + r.id">
                            <span class="ps-tag" :class="'ps-tag--' + r.forwarding.replace(' ', '-')"
                                  :data-testid="'ps-tag-' + r.id">{{ r.forwarding }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <p v-if="database" class="ps-scope text-muted" data-testid="ps-scope">
                Locals in <code>{{ database }}</code>. The .com and .ca sides are
                separate databases, so this lists the one you are signed in to.
            </p>
        </div>
    </div>
</template>

<script>
/**
 * #1893 step 1 — the SA phone sheet. Read-only; no call control.
 *
 * ed, 2026-09-11 13:31: *"waiting on SA phone sheet"*. Steps 2 (a reachable-as
 * column) and 3 (transfer/hold via ARI) are separate issues and are not here.
 *
 * ⚠ Consent is read on every load and never cached. A local clearing the box
 * must disappear on the next load — a stale consent is the one error this page
 * cannot be allowed to make, so there is no client-side store behind it.
 */
import { mapGetters } from 'vuex';
import service from '../../services.js';
import { sheetRows, localsIn, filterByLocal, emptyMessage, cellClassFor as forwardingCellClass } from '../../phoneSheet.js';

export default {
    name: 'SaPhoneSheet',
    data() {
        return {
            rows: [],
            database: '',
            local: '',
            loading: false,
            error: '',
            denied: false,
            emptyText: emptyMessage(),
        };
    },
    computed: {
        ...mapGetters({
            userToken: 'userToken',
            userRole: 'userRole',
            userClientID: 'userClientID',
        }),
        locals() { return localsIn(this.rows); },
        visible() { return filterByLocal(this.rows, this.local); },
        callableCount() { return this.visible.filter((r) => r.callable).length; },
    },
    mounted() { this.load(); },
    methods: {
        /**
         * #1917 — from the tested module, so the template carries no rule of its
         * own. ⚠ The import is ALIASED: a method named `cellClassFor` calling a
         * bare `cellClassFor` resolves lexically to the import today, but it
         * reads exactly like infinite recursion and one refactor to `this.` makes
         * it so. Not worth leaving as a puzzle.
         */
        cellClassFor(row) { return forwardingCellClass(row); },
        load() {
            this.loading = true;
            this.error = '';
            this.denied = false;
            service.get('superadmin/phone-sheet', {
                'Authorization': this.userToken,
                'cs3-context-role': this.userRole,
                'cs3-context-clientId': this.userClientID,
            }).then((res) => {
                this.loading = false;
                const r = (res && res.data && res.data.result) || {};
                // The filter runs on the SERVER too; applying the rules again here
                // means a row without consent cannot be rendered even if the
                // endpoint ever loosened.
                this.rows = sheetRows(r.rows || []);
                this.database = r.database || '';
            }).catch((err) => {
                this.loading = false;
                const code = err && err.response && err.response.status;
                if (code === 403) { this.denied = true; return; }
                this.error = 'Could not load the phone sheet. Please try again.';
                console.log(err);
            });
        },
    },
};
</script>

<style scoped>
.ps-lede { color: #6b7280; margin-top: 4px; }
.ps-controls { display: flex; gap: 16px; align-items: center; margin: 12px 0; }
.ps-count { color: #6b7280; font-size: 12px; }
.ps-table { width: 100%; border-collapse: collapse; }
.ps-table th, .ps-table td { text-align: left; padding: 8px 12px; border-bottom: 1px solid #e3e6ec; }
.ps-table th { font-size: 12px; text-transform: uppercase; color: #6b7280; }
.ps-contact { color: #6b7280; }
.ps-number { font-variant-numeric: tabular-nums; }
.ps-number--unreadable { color: #a15c00; }
.ps-unreadable-note { display: block; color: #a15c00; font-size: 11px; }
.ps-tag { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; white-space: nowrap; }
.ps-tag--allowed { background: #e7f6ec; color: #1f7a3d; }
/* ⚠ #1917 — AN ACCESSIBILITY REQUIREMENT, NOT STYLING. ed: "yes please, im
   blind, background fill".

   The first cut reused the pill's #e7f6ec, which measures 1.12:1 against a white
   page — a tint, not a fill, and effectively invisible. That is WHY it did not
   register, and it is why the fix is a SOLID fill rather than a stronger tint.

   #166534 on white and #ffffff on #166534 both measure 7.13:1 — WCAG AAA, in
   BOTH directions: the word has to be readable on the fill, and the fill has to
   be findable against the page. The second is the one that failed.

   The test COMPUTES these ratios from this stylesheet rather than asserting the
   hex, so a palette change that keeps the contrast still passes and a drift back
   to pale fails. Do not go lighter than #15803d. */
.ps-cell--allowed { background: #166534; color: #ffffff; font-weight: 600; }

/* ⚠ The pill must not fight the fill: a pale chip on a dark green cell is the
   same invisibility one layer in. The WORD stays — colour is never the only
   carrier. */
.ps-cell--allowed .ps-tag { background: transparent; color: #ffffff; font-weight: 600; }
.ps-tag--not-allowed { background: #f1f2f4; color: #6b7280; }
.ps-tag--no-number { background: #fdf1dc; color: #a15c00; }
.ps-empty, .ps-denied, .ps-error { padding: 16px; color: #6b7280; }
.ps-denied, .ps-error { color: #b91c1c; }
.ps-scope { font-size: 12px; margin-top: 12px; }
</style>
